import api from "@/app/api";
import { buildCache } from "@/app/caching/buildCache";
import type { IHasItemsCache } from "@/app/caching/caches/itemCache";
import type { IHasPackageTypesCache } from "@/app/caching/caches/packageTypesCache";
import type { IHasProductBrandsCache } from "@/app/caching/caches/productBrandsCache";
import type { IHasStockGroupsCache } from "@/app/caching/caches/stockGroupsCache";
import type { IHasSupplierManufacturersCache } from "@/app/caching/caches/supplierManufacturersCache";
import type { IHasUnitsOfMeasureCache } from "@/app/caching/caches/unitsOfMeasureCache";
import type { IHasVatCodesCache } from "@/app/caching/caches/vatCodesCache";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import {
  itemService,
  type Item,
  type PurchasingDetails,
} from "@/app/services/generated/itemService";
import type Guid from "@/app/types/common/guid";
import type { Maybe } from "@/components/utils/typeHelpers";
import tenantStore from "@/store/modules/tenantStore";

export function itemUom(item: Maybe<Item>, cache: IHasUnitsOfMeasureCache) {
  return cache.unitsOfMeasure.getById(item?.details.unitOfMeasureId) ?? null;
}

export function itemPackageType(
  item: Maybe<Item>,
  cache: IHasPackageTypesCache
) {
  return cache.packageTypes.getById(item?.packaging?.packageTypeId) ?? null;
}

export function itemIdToUom(
  itemId: Maybe<Guid>,
  cache: IHasItemsCache & IHasUnitsOfMeasureCache
) {
  return itemUom(cache.items.getById(itemId), cache);
}

export function itemStockGroup(item: Maybe<Item>, cache: IHasStockGroupsCache) {
  return cache.stockGroups.getById(item?.stock?.stockGroupId);
}

export function itemManufacturer(
  item: Maybe<Item>,
  cache: IHasSupplierManufacturersCache
) {
  return cache.supplierManufacturers.getById(item?.alcoholDuty?.manufacturerId);
}

export function itemResolvedTastingNotes(
  item: Maybe<Item>,
  cache: IHasProductBrandsCache
) {
  return item?.labelling.labelUsesBrandTastingNotes
    ? cache.productBrands.getById(item.invoicing?.productBrandId)?.tastingNotes
    : item?.labelling.labelTastingNotes;
}

export function itemResolvedIngredientsAndAllergens(
  item: Maybe<Item>,
  cache: IHasProductBrandsCache
) {
  return item?.labelling.labelUsesBrandIngredientsAndAllergens
    ? cache.productBrands.getById(item.invoicing?.productBrandId)
        ?.ingredientsAndAllergens
    : item?.labelling.labelIngredientsAndAllergens;
}

export function itemBestBeforeDate(item: Maybe<Item>) {
  const bestBeforeDays = item?.labelling.bestBeforeDays;
  return bestBeforeDays ? tenantService.today.plusDays(bestBeforeDays) : null;
}

export function itemIsTraceable(
  item: Maybe<Item>,
  cache: IHasPackageTypesCache
) {
  return (
    cache.packageTypes.getById(item?.packaging.packageTypeId)?.isTraceable ??
    false
  );
}

export function itemIsReturnable(
  item: Maybe<Item>,
  cache: IHasPackageTypesCache
) {
  return (
    cache.packageTypes.getById(item?.packaging.packageTypeId)?.isReturnable ??
    false
  );
}

export function itemDefaultSupplierId(item: Maybe<Item>) {
  return (
    item?.purchasingDetails.find((p: { isDefault: boolean }) => p.isDefault)
      ?.supplierId ?? null
  );
}

export function itemDefaultSupplier(
  item: Maybe<Item>,
  cache: IHasSupplierManufacturersCache
) {
  return (
    cache.supplierManufacturers.getById(itemDefaultSupplierId(item)) ?? null
  );
}

export function itemPurchasingDetailsForSupplierId(
  item: Maybe<Item>,
  supplierId: Maybe<Guid>
) {
  return (
    item?.purchasingDetails.find(
      (p) => supplierId && supplierId.equals(p.supplierId)
    ) ??
    item?.purchasingDetails.find((p) => !p.supplierId) ??
    null
  );
}

export function purchasingDetailsPricePerUnit(
  purchasingDetails: PurchasingDetails
) {
  return purchasingDetails?.purchasePrice && purchasingDetails?.purchasePackSize
    ? purchasingDetails?.purchasePrice / purchasingDetails?.purchasePackSize
    : null;
}

export function itemUsesHopAlpha(item: Maybe<Item>) {
  return (
    tenantStore.state.tenantSettings.usesHopAlpha &&
    !!item?.materialDetails?.defaultHopAlpha
  );
}

export function itemIdUsesHopAlpha(cache: IHasItemsCache, itemId: Maybe<Guid>) {
  return itemUsesHopAlpha(cache.items.getById(itemId));
}

export async function itemsApplyCostPriceUpdates(
  updates: readonly { itemId: Guid; newCostPrice: number }[]
) {
  if (updates.length > 0) {
    const cache = await buildCache().withItems().buildAndUpdate();

    const itemsToSave = updates.map((update) => {
      const toSave = cache.items.forId(update.itemId).clone();
      toSave.details.costPrice = update.newCostPrice;
      return toSave;
    });

    await Promise.all(
      itemsToSave.map((item) => itemService.saveItem(api.tenantId, item))
    );
  }
}

export function itemVatCode(item: Maybe<Item>, cache: IHasVatCodesCache) {
  return cache.vatCodes.getById(item?.vat?.vatCodeId) ?? null;
}
